/**
 * Footer and header layout maths, mirrored from backend/utils/report-helpers.js.
 *
 * The preview drew its own footer row at fixed thirds and left URLs as plain
 * text, so an author laying one out saw a different footer from the one the
 * report produced. These are the resolvers both renderers use, copied verbatim
 * -- verify-report-bands.js runs the two against the same configs and fails if
 * they disagree, which is the only thing keeping a copy honest.
 *
 * GENERATED from the backend source. Do not hand-edit: change report-helpers.js
 * and regenerate, or the parity check will catch the drift anyway.
 */

/**
 * The header/footer layout, in one shape both renderers and the editor agree on.
 *
 *   zones  — one row split left | centre | right, which is what a report header
 *            actually is and how Word lays one out. This is where "justify" lives.
 *   blocks — anything stacked beneath that row, for the cases a single row
 *            cannot express.
 *
 * Templates written before this existed carry flat fields (text, logoData,
 * showLogo, showPageNumber, imageData...). Rather than migrate that data, those
 * are mapped onto zones on read, in the arrangement they already rendered as:
 * logo left, text centre, page number right. So an untouched template keeps its
 * exact appearance, and only saving from the new editor writes `zones`.
 */
export function resolveHeaderFooterLayout(config, kind) {
  const cfg = config || {};
  if (cfg.zones || cfg.blocks) {
    return { zones: cfg.zones || {}, blocks: (cfg.blocks || []).filter(Boolean) };
  }

  const zones = {};
  if (kind === 'footer') {
    if (cfg.imageData) {
      zones.left = { type: 'image', imageData: cfg.imageData,
                     width: cfg.imageWidth || 80, height: cfg.imageHeight || 25 };
    }
    if (cfg.text) zones.center = { type: 'text', text: cfg.text };
    if (cfg.showPageNumber !== false) zones.right = { type: 'pageNumber', format: 'ofTotal' };
  } else {
    if (cfg.showLogo !== false) {
      // imageData null means "fall back to the template logo" — the renderer
      // resolves that, since only it has the template to hand.
      zones.left = { type: 'image', imageData: cfg.logoData || null, useTemplateLogo: !cfg.logoData,
                     width: cfg.logoWidth || 60, height: cfg.logoHeight || 20 };
    }
    if (cfg.text) zones.center = { type: 'text', text: cfg.text };
    if (cfg.showPageNumber) zones.right = { type: 'pageNumber', format: 'current' };
  }
  return { zones, blocks: [] };
}

/**
 * How wide each of the three zones is, as percentages that total 100.
 *
 * The row was fixed equal thirds, which is right for "logo | title | page
 * number" and wrong for everything else: on a Letter page the centre third is
 * 2.26in, so a one-line accreditation statement wrapped to three lines inside
 * it while two thirds of the row sat empty. The widths are stored as
 * percentages rather than twips because the PDF, the DOCX and the preview each
 * know their own content width and none of them agree on it.
 *
 * Anything missing, non-numeric or negative falls back to the even split, and
 * the three are rescaled to total 100 so a careless 50/50/50 still lays out.
 */
export const HF_ZONE_WIDTHS = { left: 33, center: 34, right: 33 };

export function headerFooterZoneWidths(config) {
  const asked = (config || {}).zoneWidths;
  if (!asked || typeof asked !== 'object') return { ...HF_ZONE_WIDTHS };

  const read = (key) => {
    const n = Number(asked[key]);
    return Number.isFinite(n) && n >= 0 ? n : HF_ZONE_WIDTHS[key];
  };
  const raw = { left: read('left'), center: read('center'), right: read('right') };
  const total = raw.left + raw.center + raw.right;
  // All three zeroed is a template saying nothing rather than a template asking
  // for a zero-width row, so it reads as "unset".
  if (total <= 0) return { ...HF_ZONE_WIDTHS };

  const scaled = {
    left: (raw.left / total) * 100,
    center: (raw.center / total) * 100,
    right: (raw.right / total) * 100,
  };
  // Rounded to whole percent, with the remainder pushed into the centre so the
  // three always total exactly 100 and no renderer has to cope with 99 or 101.
  const left = Math.round(scaled.left);
  const right = Math.round(scaled.right);
  return { left, center: 100 - left - right, right };
}

export const HF_LINK_COLOR = '#0563c1';

export const HF_URL_RE = /(https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+)/g;

export const HF_URL_TRAIL = /[.,;:!?)\]}>'"]+$/;

export function splitLinkedText(value) {
  const text = String(value == null ? '' : value);
  if (!text) return [];

  const runs = [];
  let at = 0;
  HF_URL_RE.lastIndex = 0;
  let m;
  while ((m = HF_URL_RE.exec(text)) !== null) {
    let found = m[0];
    const trail = found.match(HF_URL_TRAIL);
    if (trail) found = found.slice(0, found.length - trail[0].length);
    if (!found) continue;

    if (m.index > at) runs.push({ text: text.slice(at, m.index), link: null });
    runs.push({ text: found, link: found.startsWith('www.') ? `https://${found}` : found });
    at = m.index + found.length;
  }
  if (at < text.length) runs.push({ text: text.slice(at), link: null });
  return runs.length ? runs : [{ text, link: null }];
}

/**
 * Footer text split into lines, then each line into link runs.
 *
 * The text box is multi-line so that a logo in the left zone can sit beside
 * several centred lines in one cell; before this each line had to be its own
 * stacked block, which pushed the logo onto a row of its own above them.
 */
export function headerFooterTextLines(value) {
  return String(value == null ? '' : value)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(splitLinkedText);
}

/** The colour a link run is drawn in, honouring a per-part override. */
export function headerFooterLinkColor(part) {
  const asked = part && typeof part.linkColor === 'string' ? part.linkColor.trim() : '';
  return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(asked) ? asked : HF_LINK_COLOR;
}

/** True when a zone/block holds nothing worth rendering. */
export function isEmptyPart(part) {
  if (!part || !part.type || part.type === 'none') return true;
  if (part.type === 'text') return !String(part.text || '').trim();
  if (part.type === 'image') return !part.imageData && !part.useTemplateLogo;
  return false;   // pageNumber and rule are always renderable
}
